/**
 * Copy, with the confirmation window the copy field and the fix request
 * button both need, and an explicit failure flag so the fallback path (open
 * the preview with the text pre selected) is reachable rather than theoretical.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { captureEvent } from '../analytics/posthog';

export interface Clipboard {
  copy: (text: string, label?: string) => Promise<boolean>;
  copied: boolean;
  failed: boolean;
}

export function useClipboard(resetAfterMs = 1500): Clipboard {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = useCallback(
    async (text: string, label = 'unnamed') => {
      window.clearTimeout(timer.current);
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setFailed(false);
        captureEvent('clipboard_copy_success', {
          label,
          char_length: text.length,
          screen_path: typeof window !== 'undefined' ? window.location.pathname : '',
        });
        timer.current = window.setTimeout(() => setCopied(false), resetAfterMs);
        return true;
      } catch (err) {
        setCopied(false);
        setFailed(true);
        captureEvent('clipboard_copy_failed', {
          label,
          char_length: text.length,
          error: err instanceof Error ? err.message : String(err),
          screen_path: typeof window !== 'undefined' ? window.location.pathname : '',
        });
        return false;
      }
    },
    [resetAfterMs],
  );

  return { copy, copied, failed };
}
